import { type Database } from "@fitness-app/supabase";

import { type Dish } from "./Dish";
import { type Ingredient } from "./Ingredient";

export type DishIssueEntity = Database["public"]["Tables"]["dish_issue"]["Row"];

export enum DishIssueStatus {
  Reported = "reported",
  Resolved = "resolved",
  Closed = "closed",
}

/** TRE-2414 - who reported: a trainer from a meals plan, or a client from her diet tracker. */
export enum DishIssueSource {
  Trainer = "trainer",
  Trainee = "trainee",
}

/**
 * What a client says is wrong with a product or a dish; `correction` (products only) carries her
 * proposed changes, `ingredients` and `recipe` are for dishes, `duplicate` for products.
 */
export enum IngredientIssueCategory {
  Nutrients = "nutrients",
  Name = "name",
  Photo = "photo",
  Duplicate = "duplicate",
  Other = "other",
  Correction = "correction",
  Ingredients = "ingredients",
  Recipe = "recipe",
}

export enum IngredientIssueOutcome {
  Resolved = "resolved",
  Rejected = "rejected",
}

/**
 * Product fields a client may correct and the team may apply - the same list
 * `private.validate_ingredient_changes` accepts (`20261023120000_trainee_ingredient_issues.sql`).
 */
export const INGREDIENT_CORRECTION_FIELDS = [
  "name",
  "brand",
  "manufacturer",
  "image",
  "nutrients",
  "calories",
  "measures",
  "packageSize",
  "rawIngredients",
  "liquid",
  "unit",
  "barCode",
  "mainCategory",
  "isVegan",
  "isVegetarian",
  "isDairyFree",
  "isGlutenFree",
  "isHighProtein",
] as const satisfies readonly (keyof Ingredient)[];

export type IngredientCorrection = Partial<Pick<Ingredient, (typeof INGREDIENT_CORRECTION_FIELDS)[number]>>;

/** `dish_issue.metadata` of a client's product report, as `report_ingredient_issue` and `resolve_trainee_issue` write it. */
export interface TraineeIngredientIssueMetadata {
  ingredientName: string;
  /** The product's correctable fields when the client reported it - the left side of the diff. */
  before: IngredientCorrection;
  proposedChanges?: IngredientCorrection;
  photoUrl?: string;
  outcome?: IngredientIssueOutcome;
  appliedChanges?: IngredientCorrection;
  resolvedAt?: string;
}

/** `dish_issue.metadata` of a client's dish report, as `report_dish_issue` and `resolve_trainee_issue` write it. */
export interface TraineeDishIssueMetadata {
  dishName: string;
  photoUrl?: string;
  outcome?: IngredientIssueOutcome;
  updateTemplates?: boolean;
  resolvedAt?: string;
}

/** The correctable fields of `next` that differ from `previous` - what a client's edit proposes. */
export const diffIngredientCorrection = (
  previous: IngredientCorrection,
  next: IngredientCorrection,
): IngredientCorrection => {
  const changes: Record<string, unknown> = {};
  for (const field of INGREDIENT_CORRECTION_FIELDS) {
    if (field in next && JSON.stringify(next[field] ?? null) !== JSON.stringify(previous[field] ?? null)) {
      changes[field] = next[field] ?? null;
    }
  }
  return changes as IngredientCorrection;
};

export type DishIssue = Omit<DishIssueEntity, "status" | "source" | "categories"> & {
  status: DishIssueStatus;
  source: DishIssueSource;
  /** A client's report only; she may pick several, `other` and `correction` always stand alone. */
  categories: IngredientIssueCategory[] | null;
};

export type DishIssueWithDishOrIngredient =
  | (DishIssue & {
      dish: Dish;
    })
  | (DishIssue & { ingredient: Ingredient });

export type DishIssueWithOwner = DishIssueWithDishOrIngredient & {
  owner: {
    firstName: string;
    lastName: string;
    avatarUrl: string;
    email: string;
  };
  reporter: {
    firstName: string;
    lastName: string;
    avatarUrl: string;
    email: string;
  };
};
